import { AlertTriangle, CheckCircle } from 'lucide-react';

interface Incident {
    id: string;
    type: 'bovin' | 'eboulement' | 'promeneur' | 'autre';
    pk: number;
    timestamp: number;
    description: string;
}

interface IncidentValidatorProps {
    incident: Incident;
    onResolve: (stillPresent: boolean) => void;
}

export default function IncidentValidator({ incident, onResolve }: IncidentValidatorProps) {
    const icon = incident.type === 'bovin' ? '🐄' : incident.type === 'eboulement' ? '⚠️' : '🚶';

    return (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
            <div className="bg-slate-900 border border-white/10 p-8 rounded-[3rem] shadow-2xl max-w-md w-full mx-4 space-y-6 text-center border-t-4 border-t-red-500">
                <div className="flex justify-center">
                    <div className="w-20 h-20 bg-red-500/10 rounded-full flex items-center justify-center text-4xl animate-pulse">
                        {icon}
                    </div>
                </div>

                <div className="space-y-2">
                    <h2 className="text-white font-black uppercase text-xl tracking-tighter">Levée de Doute</h2>
                    <p className="text-slate-400 text-sm">
                        Vous venez de franchir la zone de signalement : <br />
                        <span className="text-red-400 font-bold uppercase tracking-widest text-xs">
                            {incident.type === 'bovin' ? 'Bovins sur la voie' : incident.type.toUpperCase()} (PK {incident.pk.toFixed(3)})
                        </span>
                    </p>
                </div>

                <div className="bg-black/40 p-4 rounded-2xl border border-white/5 italic text-slate-300 text-sm">
                    « {incident.description} »
                </div>

                <p className="text-white font-bold text-lg leading-tight uppercase tracking-tighter">
                    Le danger est-il toujours présent ?
                </p>

                <div className="grid grid-cols-2 gap-4">
                    <button
                        onClick={() => onResolve(true)}
                        className="flex flex-col items-center gap-2 bg-slate-800 hover:bg-slate-700 text-white p-4 rounded-2xl transition-all border border-white/5 active:scale-95 group"
                    >
                        <AlertTriangle className="w-6 h-6 text-orange-500 group-hover:scale-110 transition-transform" />
                        <span className="text-[10px] font-black uppercase tracking-widest">Oui, toujours présent</span>
                    </button>

                    <button
                        onClick={() => onResolve(false)}
                        className="flex flex-col items-center gap-2 bg-green-600 hover:bg-green-500 text-white p-4 rounded-2xl transition-all shadow-lg active:scale-95 group"
                    >
                        <CheckCircle className="w-6 h-6 text-white group-hover:scale-110 transition-transform" />
                        <span className="text-[10px] font-black uppercase tracking-widest">Non, zone libérée</span>
                    </button>
                </div>

                <p className="text-[9px] text-slate-500 uppercase tracking-widest font-bold pt-2">
                    Votre réponse mettra à jour la carte de tous les utilisateurs.
                </p>
            </div>
        </div>
    );
}
